// src/authpages/dashboard/WeeklyActivityChart.js
import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { getUserStudyStats } from './StudyStatsService';

const WeeklyActivityChart = () => {
  const [weeklyProgress, setWeeklyProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [maxValue, setMaxValue] = useState(120); // Default max value for scaling
  const [refreshTime, setRefreshTime] = useState(new Date());

  // Calculate weekly progress from study days data
  const calculateWeeklyProgress = useCallback((studyDays) => {
    console.log("Calculating weekly progress with study days:", studyDays);
    const lastSevenDays = [];
    
    const currentDate = new Date();
    const currentDay = currentDate.getDay();
    
    // Calculate the date of Monday (start of week)
    const mondayOffset = currentDay === 0 ? 6 : currentDay - 1;
    const monday = new Date(currentDate);
    monday.setDate(currentDate.getDate() - mondayOffset);
    
    // Generate data for each day of the week
    for (let i = 0; i < 7; i++) {
      const day = new Date(monday);
      day.setDate(monday.getDate() + i);
      
      const dateStr = day.toISOString().split('T')[0];
      
      lastSevenDays.push({
        date: dateStr,
        day: day.toLocaleDateString('en-US', { weekday: 'short' }),
        minutes: studyDays?.[dateStr] || 0
      });
    }
    
    // Find the maximum value to better scale the chart
    const maxMinutes = Math.max(...lastSevenDays.map(d => d.minutes), 60); // At least 60 min
    setMaxValue(Math.ceil(maxMinutes * 1.2)); // Add 20% padding
    
    console.log("Calculated weekly progress:", lastSevenDays);
    return lastSevenDays;
  }, []);

  // Fetch weekly activity data from Firebase
  const fetchWeeklyActivity = useCallback(async (forceRefresh = false) => {
    try {
      if (forceRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      console.group("Fetching Weekly Activity");
      console.log("Force Refresh:", forceRefresh);
      
      // Get study stats from Firebase
      const statsData = await getUserStudyStats();
      
      if (!statsData) {
        console.warn("No stats data received");
        setLoading(false);
        setRefreshing(false);
        console.groupEnd();
        return;
      }
      
      console.log("Raw stats data received:", statsData);
      
      // Calculate weekly progress
      const weekProgress = calculateWeeklyProgress(statsData.studyDays || {});
      setWeeklyProgress(weekProgress);
      
      setLoading(false);
      setRefreshing(false);
      setRefreshTime(new Date());
      
      console.groupEnd();
    } catch (error) {
      console.error('Error fetching weekly activity:', error);
      setLoading(false);
      setRefreshing(false);
      console.groupEnd();
    }
  }, [calculateWeeklyProgress]);

  // Handle manual refresh
  const handleRefresh = () => {
    fetchWeeklyActivity(true);
  };

  // Effect to load data on component mount and set up automatic refresh
  useEffect(() => {
    let isMounted = true;
    
    // Function to fetch initial data
    const loadInitialData = async () => {
      if (isMounted) {
        await fetchWeeklyActivity();
      }
    };
    
    loadInitialData();

    const checkForCompletedSessions = () => {
      const sessionCompleted = localStorage.getItem('studySessionCompleted');
      const completedAt = localStorage.getItem('studySessionCompletedAt');
      
      if (sessionCompleted === 'true' && completedAt) {
        // Check if the completion happened recently (within last 10 minutes instead of 5)
        const completionTime = new Date(completedAt);
        const now = new Date();
        const timeDiffMinutes = (now - completionTime) / (1000 * 60);
        
        if (timeDiffMinutes < 10) {
          console.log("Recent study session detected, refreshing chart");
          fetchWeeklyActivity(true);
          
          // Only clear the flag after successful refresh
          setTimeout(() => {
            localStorage.setItem('studySessionCompleted', 'false');
            console.log("Cleared studySessionCompleted flag");
          }, 2000);
        }
      }
    };
    
    // Check for completed sessions more frequently
    const sessionCheckInterval = setInterval(checkForCompletedSessions, 5000); // Check every 5 seconds
    
    // Also do a regular refresh in case data changes elsewhere
    const dataRefreshInterval = setInterval(() => {
      if (isMounted) {
        fetchWeeklyActivity();
      }
    }, 60000); // Refresh every minute
    
    // Listen for refresh events from the timer
    const refreshEventHandler = (event) => {
      console.log("Received refresh event:", event);
      if (isMounted) {
        fetchWeeklyActivity(true);
      }
    };
    
    window.addEventListener('refreshStudyStats', refreshEventHandler);
    
    // Initial check for any recent sessions
    checkForCompletedSessions();
    
    return () => {
      isMounted = false;
      clearInterval(sessionCheckInterval);
      clearInterval(dataRefreshInterval);
      window.removeEventListener('refreshStudyStats', refreshEventHandler);
    };
  }, [fetchWeeklyActivity]);

  if (loading) {
    return <div className="text-center text-gray-500 p-6">Loading weekly activity...</div>;
  }

  return (
    <div className="mt-4 bg-white shadow-sm rounded-lg">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium">Weekly Activity</h3>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">
              Last updated: {refreshTime.toLocaleTimeString()}
            </span>
            <button 
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center space-x-1 px-2 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
            </button>
          </div>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          {/* Chart label with max value */}
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>0 min</span>
            <span>{maxValue} min</span>
          </div>
          
          {/* Bar chart */}
          <div className="grid grid-cols-7 gap-2 h-48 items-end border-b border-gray-200">
            {weeklyProgress.map((day, index) => {
              // Calculate height percentage based on max value
              const heightPercentage = Math.max(1, (day.minutes / maxValue) * 100);
              const barColor = day.minutes > 0 
                ? (day.minutes > maxValue / 2 ? 'bg-blue-600' : 'bg-blue-500') 
                : 'bg-gray-300';
              
              return (
                <div key={index} className="flex flex-col items-center h-full">
                  {/* Study time label */}
                  <div className="text-xs font-medium mb-1">
                    {day.minutes > 0 ? `${day.minutes}m` : '-'}
                  </div>
                  
                  {/* Bar */}
                  <div 
                    className={`w-full ${barColor} rounded-t-sm hover:opacity-80 transition-all`}
                    style={{ height: `${heightPercentage}%` }}
                    title={`${day.day}: ${day.minutes} minutes`}
                  ></div>
                  
                  {/* Day label */}
                  <div className="text-xs text-gray-600 mt-2">{day.day}</div>
                </div>
              );
            })}
          </div>
          
          {/* Chart footer */}
          <div className="mt-3 text-xs text-gray-500 text-center">
            {weeklyProgress.some(day => day.minutes > 0) 
              ? 'Study minutes per day this week' 
              : 'No study activity recorded this week yet'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeeklyActivityChart;